import React, { useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  IconButton,
  Collapse,
  Alert,
  Box,
  CircularProgress,
  Fade,
  Backdrop,
  useTheme,
  Paper,
  Stack,
  Divider,
  TextField,
  Button,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete,
  Warning,
  Cancel,
  PersonRemove,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const DeleteProfile = ({ profile, onDelete, renderTrigger }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const theme = useTheme();

  const handleClose = () => {
    setShowModal(false);
    setError(null);
    setConfirmText("");
  };

  const handleShow = () => setShowModal(true);

  async function removeProfile(e) {
    e.preventDefault();

    if (confirmText !== profile.username) {
      setError(`Please type "${profile.username}" to confirm deletion.`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { status } = await axios.delete(
        `https://le-travaille-server.onrender.com/user/${profile.user_id}`,
        { headers: { token: token } },
      );

      if (status === 200) {
        toast.success(
          "Profile deleted successfully. We're sad to see you go! 👋",
        );
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user id");
        if (onDelete) onDelete(profile.user_id);
        handleClose();
        navigate("/auth/signup");
      }
    } catch (err) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          "Failed to delete profile. Please try again.",
      );
    }
  }

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleShow)
      ) : (
        <Button
          variant="outlined"
          color="error"
          onClick={handleShow}
          startIcon={<Delete />}
          fullWidth
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            py: 1.2,
            borderWidth: 2,
            borderColor: "#c62828",
            color: "#c62828",
            "&:hover": {
              borderWidth: 2,
              borderColor: "#b71c1c",
              backgroundColor: "rgba(198, 40, 40, 0.04)",
              transform: "scale(1.02)",
            },
            transition: "transform 0.2s ease-in-out",
          }}
        >
          Delete Account
        </Button>
      )}

      <Dialog
        open={showModal}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 400 }}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: { backdropFilter: "blur(4px)" },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, #c62828 0%, #d32f2f 100%)`,
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Warning sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Delete Account
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.15)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Typography variant="body2" color="error.dark">
              <strong>Warning:</strong> This action is irreversible. All your
              data, including your profile information, will be permanently
              deleted.
            </Typography>

            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Profile to delete:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                }}
              >
                <PersonRemove color="error" />
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {profile.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {profile.email}
                  </Typography>
                </Box>
              </Paper>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Type{" "}
                <strong style={{ color: theme.palette.error.main }}>
                  {profile.username}
                </strong>{" "}
                to confirm deletion:
              </Typography>
              <TextField
                fullWidth
                value={confirmText}
                onChange={(e) => {
                  setConfirmText(e.target.value);
                  setError(null);
                }}
                placeholder={`Type "${profile.username}"`}
                variant="outlined"
                error={Boolean(error)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Box>

            <Collapse in={Boolean(error)}>
              <Alert
                severity="error"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setError(null)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                sx={{ borderRadius: 2 }}
              >
                {error}
              </Alert>
            </Collapse>

            <Divider />

            <Box>
              <Typography variant="caption" color="text.secondary">
                What happens after deletion:
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                <li>
                  <Typography variant="caption" color="text.secondary">
                    All your data will be permanently removed
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption" color="text.secondary">
                    You will be logged out automatically
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption" color="text.secondary">
                    You can create a new account anytime
                  </Typography>
                </li>
              </Box>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleClose}
            variant="contained"
            startIcon={<Cancel />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#f8f9fa",
              color: "#212529",
              border: "1px solid #d3d3d3",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#e2e6ea",
                borderColor: "#c1c9d0",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={removeProfile}
            variant="contained"
            color="error"
            startIcon={!isLoading && <Delete />}
            disabled={isLoading || confirmText !== profile.username}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
              "&:hover": {
                transform: "scale(1.05)",
                backgroundColor: "#b71c1c",
              },
              transition: "transform 0.2s ease-in-out",
              "&:disabled": {
                opacity: 0.5,
                transform: "none",
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Delete Permanently"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteProfile;
